import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  OnDestroy,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';

@Component({
  selector: 'viewer-volume-button',
  templateUrl: './viewer-volume-button.component.html',
  styleUrl: './viewer-volume-button.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ViewerVolumeButtonComponent implements OnDestroy {
  @Input() muted: boolean = false;
  @Input() volume: number = 1;
  @Output() toggleMute: EventEmitter<void> = new EventEmitter<void>();
  @Output() volumeChange: EventEmitter<number> = new EventEmitter<number>();

  private tooltipEl: HTMLDivElement | null = null;

  @ViewChild('sliderTrack') sliderTrack!: ElementRef<HTMLDivElement>;
  @ViewChild('volumeButton') volumeButton!: ElementRef<HTMLButtonElement>;

  private isDragging: boolean = false;

  constructor(private readonly renderer: Renderer2) {}

  get fillPercent(): number {
    return (this.muted ? 0 : this.volume) * 100;
  }

  public onTrackMouseDown(event: MouseEvent): void {
    this.isDragging = true;
    this.updateVolumeFromEvent(event);

    const onMouseMove = (e: MouseEvent) => {
      if (this.isDragging) {
        this.updateVolumeFromEvent(e);
      }
    };

    const onMouseUp = () => {
      this.isDragging = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  private updateVolumeFromEvent(event: MouseEvent): void {
    const track = this.sliderTrack.nativeElement;
    const rect = track.getBoundingClientRect();
    let ratio = (event.clientX - rect.left) / rect.width;
    ratio = Math.max(0, Math.min(1, ratio));
    this.volumeChange.emit(ratio);
  }

  public showTooltip(): void {
    const button: any = this.volumeButton?.nativeElement;
    if (!button) return;

    if (!this.tooltipEl) {
      this.tooltipEl = this.renderer.createElement('div') as HTMLDivElement;
      this.tooltipEl.className = 'viewer-volume-tooltip';
      this.tooltipEl.textContent = 'Volume';

      this.tooltipEl.style.position = 'fixed';
      this.tooltipEl.style.pointerEvents = 'none';
      this.tooltipEl.style.zIndex = '20000';
      this.tooltipEl.style.visibility = 'hidden';
      this.tooltipEl.style.left = '0px';
      this.tooltipEl.style.top = '0px';

      this.renderer.appendChild(document.body, this.tooltipEl);
    }

    this.positionTooltip();
    this.tooltipEl.style.visibility = 'visible';
  }

  public hideTooltip(): void {
    if (!this.tooltipEl) return;
    this.tooltipEl.style.visibility = 'hidden';
  }

  private positionTooltip(): void {
    if (!this.tooltipEl) return;

    const buttonRect: any = this.volumeButton.nativeElement.getBoundingClientRect();

    // Create headroom exactly like the old ::after tooltip:
    // bottom: calc(100% + 8px) => tooltip is 8px above the button.
    this.tooltipEl.style.left = `${buttonRect.left + buttonRect.width / 2}px`;

    // Measure after visibility is managed by caller; if height is 0, keep it near.
    const tooltipHeight: number = this.tooltipEl.getBoundingClientRect().height || 24;
    this.tooltipEl.style.top = `${buttonRect.top - 10 - tooltipHeight}px`;
    this.tooltipEl.style.transform = 'translateX(-50%)';
  }

  ngOnDestroy(): void {
    if (this.tooltipEl) {
      this.tooltipEl.remove();
      this.tooltipEl = null;
    }
  }
}
